/* External */
import { useCallback, useState, memo } from 'react';
import PropTypes from 'prop-types';
import { FormattedMessage } from 'react-intl';

/* Components */
import FocusableButton from '../FocusableButton/FocusableButton';
import CustomInputSearch from './CustomInputSearch';
import CustomKey from './CustomKey';
import FocusableDiv from '../FocusableDiv/FocusableDiv';

/* Utils */
import { noOp } from '../../utils/functions';
import { letters, numbers } from './utils';
import withCursorVisibilityFilterFocusable from '../../hocs/withCursorVisibilityFilter.hoc';
import { Direction } from '../../utils/spatialNavigation';

/* Styles */
import './CustomKeyboard.scss';

function CustomKeyboard({
  className,
  onArrowPressOut,
  onChange,
  inputPlaceholder,
  handleClearSearchTagEvent,
  initialValue,
}) {
  const [query, setQuery] = useState(initialValue);
  const handleChange = useCallback((value) => {
    setQuery(value);
    onChange(value);
  }, [onChange]);

  const addCharacter = useCallback((value) => {
    handleChange(query + value);
  }, [query, handleChange]);

  const cleanQuery = useCallback(() => {
    handleChange('');
    handleClearSearchTagEvent();
  }, [handleChange, handleClearSearchTagEvent]);

  const deleteCharacter = useCallback(() => {
    handleChange(query.slice(0, -1));
  }, [query, handleChange]);

  const addSpace = useCallback(() => {
    handleChange(`${query} `);
  }, [query, handleChange]);

  const handleArrowPress = useCallback((direction, keyprops) => {
    const { index } = keyprops;
    const isActionBtn = keyprops.className.includes('action-button');
    const isNumberKey = keyprops.className.includes('number');
    const isActionBtnLeft = isActionBtn && index === 0;
    const isActionBtnRight = isActionBtn && index === 2;
    const isLastColumnKey = (index + 1) % 7 === 0;
    const isFirstColumnKey = index % 7 === 0;
    if (
      (direction === Direction.UP && isActionBtn)
      || (direction === Direction.RIGHT && (isLastColumnKey || isActionBtnRight))
      || (direction === Direction.LEFT && (isFirstColumnKey || isActionBtnLeft))
      || (direction === Direction.DOWN && isNumberKey && index > 6)) {
      onArrowPressOut(direction, keyprops);
      return true;
    }
    return true;
  }, [onArrowPressOut]);

  return (
    <div className={`dtv-html5-c-custom-keyboard ${className}`}>
      <CustomInputSearch inputValue={query} placeholder={inputPlaceholder} />
      <FocusableDiv className="dtv-html5-c-custom-keyboard__action-buttons">
        <FocusableButton
          index={0}
          onEnterPress={addSpace}
          onArrowPress={handleArrowPress}
          className="dtv-html5-c-custom-keyboard__action-button"
        >
          <FormattedMessage id="customKeyboard.actionButton.space" />
        </FocusableButton>
        <FocusableButton
          onEnterPress={deleteCharacter}
          index={1}
          onArrowPress={handleArrowPress}
          className="dtv-html5-c-custom-keyboard__action-button"
        >
          <FormattedMessage id="customKeyboard.actionButton.delete" />
        </FocusableButton>
        <FocusableButton
          onEnterPress={cleanQuery}
          index={2}
          onArrowPress={handleArrowPress}
          className="dtv-html5-c-custom-keyboard__action-button"
        >
          <FormattedMessage id="customKeyboard.actionButton.cleanAll" />
        </FocusableButton>
      </FocusableDiv>
      <hr />
      <FocusableDiv className="dtv-html5-c-custom-keyboard__letters">
        {
          letters.map((letter, index) => (
            <CustomKey
              key={letter.character}
              index={index}
              character={letter.character}
              variation={letter.variation}
              onPress={addCharacter}
              onArrowPress={handleArrowPress}
              className="dtv-html5-c-custom-keyboard__letter"
            />
          ))
        }
      </FocusableDiv>
      <hr />
      <FocusableDiv className="dtv-html5-c-custom-keyboard__numbers">
        {
          numbers.map((number, index) => (
            <CustomKey
              key={number.character}
              index={index}
              character={number.character}
              variation={number.variation}
              onPress={addCharacter}
              onArrowPress={handleArrowPress}
              className="dtv-html5-c-custom-keyboard__number"
            />
          ))
        }
      </FocusableDiv>
    </div>
  );
}

CustomKeyboard.propTypes = {
  className: PropTypes.string,
  onArrowPressOut: PropTypes.func,
  onChange: PropTypes.func,
  inputPlaceholder: PropTypes.string,
  handleClearSearchTagEvent: PropTypes.func,
  initialValue: PropTypes.string,
};

CustomKeyboard.defaultProps = {
  className: '',
  onArrowPressOut: noOp,
  inputPlaceholder: '',
  onChange: noOp,
  handleClearSearchTagEvent: noOp,
  initialValue: '',
};

export default memo(withCursorVisibilityFilterFocusable()(memo(CustomKeyboard)));
